import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputBase,
  Button,
  Alert,
  Stack,
  Chip,
  Popover,
} from '@mui/material';
import GenericModal from './GenericModal';
import ParentChildGoalEditor from './ParentChildGoalEditor';
import { habitService } from '../services/api';
import { getCategoryColor } from '../utils/habitColors';
import { CreateHabitRequest, HabitDefinition, UpdateHabitGoalRequest } from '../types/api';

interface ParentHabitContext {
  habitId: string;
  habitDisplayName: string;
  category: string;
  goal: HabitDefinition['goal'];
  goalUnit: string;
  intervalUnit: string;
  versionSK: string;
  lastModifiedAt: string;
  isPublished: boolean;
}

interface CreateHabitModalProps {
  open: boolean;
  onClose: () => void;
  onHabitCreated?: () => void;
  parentHabit?: ParentHabitContext | null;
}

const CreateHabitModal: React.FC<CreateHabitModalProps> = ({
  open,
  onClose,
  onHabitCreated,
  parentHabit = null,
}) => {
  const [habitName, setHabitName] = useState<string>('Habit Name');
  const [description, setDescription] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryAnchorEl, setCategoryAnchorEl] = useState<null | HTMLElement>(null);
  
  // Goal-related state
  const [selectedGoalType, setSelectedGoalType] = useState<'RANGE' | 'THRESHOLD' | 'AVERAGE'>('THRESHOLD');
  const [goalUnit, setGoalUnit] = useState<string>('');
  const [intervalUnit, setIntervalUnit] = useState<string>('');
  const [thresholdValue, setThresholdValue] = useState<string>('');
  const [rangeMin, setRangeMin] = useState<string>('');
  const [rangeMax, setRangeMax] = useState<string>('');
  
  // Child habit goal-related state
  const [childThresholdValue, setChildThresholdValue] = useState<string>('');
  const [childRangeMin, setChildRangeMin] = useState<string>('');
  const [childRangeMax, setChildRangeMax] = useState<string>('');
  const [siblingHabits, setSiblingHabits] = useState<HabitDefinition[]>([]);
  const [childGoalValidationError, setChildGoalValidationError] = useState<string | null>(null);
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const availableGoalUnits = ['sessions', 'minutes', 'hours', 'count', 'accounting sessions', 'percent'];
  const availableIntervalUnits = ['day', 'week', 'month', 'year'];

  // Reset all fields when modal opens
  useEffect(() => {
    if (open) {
      // Reset all fields
      setHabitName(parentHabit ? '' : 'Habit Name');
      setDescription('');
      setSelectedCategory('');
      setSelectedGoalType('THRESHOLD');
      setGoalUnit('');
      setIntervalUnit('');
      setThresholdValue('');
      setRangeMin('');
      setRangeMax('');
      setChildThresholdValue('');
      setChildRangeMin('');
      setChildRangeMax('');
      setSaveError(null);
      setChildGoalValidationError(null);
      
      // If creating a child habit, inherit parent's goal settings and fetch siblings
      if (parentHabit) {
        if (parentHabit.goal?.type === 'THRESHOLD') {
          setSelectedGoalType('THRESHOLD');
          setThresholdValue(String(parentHabit.goal.value ?? ''));
          setRangeMin('');
          setRangeMax('');
        } else if (parentHabit.goal?.type === 'RANGE' || parentHabit.goal?.type === 'AVERAGE') {
          setSelectedGoalType(parentHabit.goal.type);
          setRangeMin(String(parentHabit.goal.min ?? ''));
          setRangeMax(String(parentHabit.goal.max ?? ''));
          setThresholdValue('');
        }
        setGoalUnit(parentHabit.goalUnit || '');
        setIntervalUnit(parentHabit.intervalUnit || '');
        
        // Fetch sibling habits
        const fetchSiblings = async () => {
          try {
            const response = await habitService.getHabit(parentHabit.habitId);
            if (response.success && response.data) {
              const childIds = response.data.childHabitIds || [];
              if (childIds.length > 0) {
                const siblings: HabitDefinition[] = [];
                for (const childId of childIds) {
                  try {
                    const childResponse = await habitService.getHabit(childId);
                    if (childResponse.success && childResponse.data) {
                      siblings.push(childResponse.data);
                    }
                  } catch (err) {
                    console.error(`Error fetching sibling ${childId}:`, err);
                  }
                }
                setSiblingHabits(siblings);
              }
            }
          } catch (err) {
            console.error('Error fetching sibling habits:', err);
          }
        };
        fetchSiblings();
      } else {
        setSiblingHabits([]);
      }
    }
  }, [open, parentHabit?.habitId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-calculate parent goal amounts from siblings + new child for child-habit creation.
  useEffect(() => {
    if (!parentHabit) return;

    if (selectedGoalType === 'THRESHOLD') {
      const siblingSum = siblingHabits.reduce((sum, s) => {
        return sum + (s.goal?.type === 'THRESHOLD' ? (s.goal.value || 0) : 0);
      }, 0);
      const childVal = parseFloat(childThresholdValue) || 0;
      setThresholdValue(String(siblingSum + childVal));
    } else {
      const siblingSumMin = siblingHabits.reduce((sum, s) => {
        return sum + (s.goal?.type !== 'THRESHOLD' ? (s.goal.min || 0) : 0);
      }, 0);
      const siblingSumMax = siblingHabits.reduce((sum, s) => {
        return sum + (s.goal?.type !== 'THRESHOLD' ? (s.goal.max || 0) : 0);
      }, 0);
      const childMin = parseFloat(childRangeMin) || 0;
      const childMax = parseFloat(childRangeMax) || 0;
      setRangeMin(String(siblingSumMin + childMin));
      setRangeMax(String(siblingSumMax + childMax));
    }
  }, [childThresholdValue, childRangeMin, childRangeMax, siblingHabits, selectedGoalType, parentHabit]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await habitService.getCategories();
        if (response.success && response.data) {
          setCategories(response.data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleCategoryClick = (event: React.MouseEvent<HTMLElement>) => {
    setCategoryAnchorEl(event.currentTarget);
  };

  const handleCategorySelect = (category: string, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setSelectedCategory(category);
    setCategoryAnchorEl(null);
  };

  const handleCategoryClose = () => {
    setCategoryAnchorEl(null);
  };

  const validateChildHabitGoal = (): { isValid: boolean; error: string } => {
    if (!parentHabit) return { isValid: true, error: '' };

    const parentGoalType = selectedGoalType;

    const parentThreshold = parseFloat(thresholdValue);
    const effectiveParentThreshold = !isNaN(parentThreshold)
      ? parentThreshold
      : parentHabit.goal?.type === 'THRESHOLD' ? (parentHabit.goal.value || 0) : 0;

    const parentMin = parseFloat(rangeMin);
    const effectiveParentMin = !isNaN(parentMin)
      ? parentMin
      : parentHabit.goal?.type !== 'THRESHOLD' ? (parentHabit.goal.min || 0) : 0;

    const parentMax = parseFloat(rangeMax);
    const effectiveParentMax = !isNaN(parentMax)
      ? parentMax
      : parentHabit.goal?.type !== 'THRESHOLD' ? (parentHabit.goal.max || 0) : 0;

    if (selectedGoalType === 'THRESHOLD') {
      if (!childThresholdValue || childThresholdValue.trim() === '') {
        return { isValid: false, error: 'Child goal amount is required' };
      }
      const childValue = parseFloat(childThresholdValue);
      if (isNaN(childValue)) {
        return { isValid: false, error: 'Child goal amount must be a valid number' };
      }
      if (childValue <= 0) {
        return { isValid: false, error: 'Child goal amount must be greater than 0' };
      }

      // Calculate sum of sibling goals
      const siblingSum = siblingHabits.reduce((sum, sibling) => {
        if (sibling.goal?.type === 'THRESHOLD') {
          return sum + (sibling.goal.value || 0);
        }
        return sum;
      }, 0);

      const parentValue = parentGoalType === 'THRESHOLD' ? effectiveParentThreshold : 0;
      const totalChild = siblingSum + childValue;

      if (totalChild > parentValue) {
        return {
          isValid: false,
          error: `Sum of child habit goal amounts cannot exceed parent goal. Total amount ${totalChild} would exceed parent goal amount ${parentValue}.`,
        };
      }
    } else {
      if (!childRangeMin || childRangeMin.trim() === '' || !childRangeMax || childRangeMax.trim() === '') {
        return { isValid: false, error: 'Both child minimum and maximum values are required' };
      }
      const childMin = parseFloat(childRangeMin);
      const childMax = parseFloat(childRangeMax);
      if (isNaN(childMin) || isNaN(childMax)) {
        return { isValid: false, error: 'Child range values must be valid numbers' };
      }
      if (selectedGoalType === 'RANGE' && (childMin < 0 || childMax < 0)) {
        return { isValid: false, error: 'Child goal amounts cannot be negative' };
      }
      if (childMin > childMax) {
        return { isValid: false, error: 'Child minimum value cannot be greater than maximum value' };
      }

      if (parentGoalType === 'RANGE') {
        // Calculate sum of sibling minimums and maximums
        const siblingSumMin = siblingHabits.reduce((sum, sibling) => {
          if (sibling.goal?.type !== 'THRESHOLD') {
            return sum + (sibling.goal.min || 0);
          }
          return sum;
        }, 0);
        const siblingSumMax = siblingHabits.reduce((sum, sibling) => {
          if (sibling.goal?.type !== 'THRESHOLD') {
            return sum + (sibling.goal.max || 0);
          }
          return sum;
        }, 0);

        const totalChildMin = siblingSumMin + childMin;
        const totalChildMax = siblingSumMax + childMax;

        if (totalChildMin > effectiveParentMin) {
          return {
            isValid: false,
            error: `Sum of child minimum goal amounts cannot exceed parent minimum goal. Total min ${totalChildMin} would exceed parent min goal ${effectiveParentMin}.`,
          };
        }
        if (totalChildMax > effectiveParentMax) {
          return {
            isValid: false,
            error: `Sum of child maximum goal amounts cannot exceed parent maximum goal. Total max ${totalChildMax} would exceed parent max goal ${effectiveParentMax}.`,
          };
        }
      }
    }

    return { isValid: true, error: '' };
  };

  const validateInputs = (): { isValid: boolean; error: string } => {
    // Check description
    if (!description || description.trim() === '') {
      return { isValid: false, error: 'Description is required' };
    }

    // Check category for top-level habits only
    if (!parentHabit && (!selectedCategory || selectedCategory.trim() === '')) {
      return { isValid: false, error: 'Category is required' };
    }

    // Check goal unit
    if (!goalUnit || goalUnit.trim() === '') {
      return { isValid: false, error: 'Goal unit is required' };
    }

    // Check interval unit for non-average goals.
    if (selectedGoalType !== 'AVERAGE' && (!intervalUnit || intervalUnit.trim() === '')) {
      return { isValid: false, error: 'Interval unit is required' };
    }

    // Validate child habit goals if creating a child
    if (parentHabit) {
      const childValidation = validateChildHabitGoal();
      if (!childValidation.isValid) {
        return childValidation;
      }
    } else {
      // Validate parent habit goals
      if (selectedGoalType === 'THRESHOLD') {
        if (!thresholdValue || thresholdValue.trim() === '') {
          return { isValid: false, error: 'Threshold value is required' };
        }
        const value = parseFloat(thresholdValue);
        if (isNaN(value)) {
          return { isValid: false, error: 'Threshold value must be a valid number' };
        }
        if (value <= 0) {
          return { isValid: false, error: 'Threshold value must be greater than 0' };
        }
      } else {
        if (!rangeMin || rangeMin.trim() === '' || !rangeMax || rangeMax.trim() === '') {
          return { isValid: false, error: 'Both minimum and maximum values are required' };
        }
        const min = parseFloat(rangeMin);
        const max = parseFloat(rangeMax);
        if (isNaN(min) || isNaN(max)) {
          return { isValid: false, error: `${selectedGoalType === 'AVERAGE' ? 'Average' : 'Range'} values must be valid numbers` };
        }
        if (selectedGoalType === 'RANGE' && (min < 0 || max < 0)) {
          return { isValid: false, error: 'Goal amounts cannot be negative' };
        }
        if (min > max) {
          return { isValid: false, error: 'Minimum value cannot be greater than maximum value' };
        }
      }
    }

    return { isValid: true, error: '' };
  };

  const handleSave = async (publish: boolean) => {
    const validation = validateInputs();
    if (!validation.isValid) {
      setSaveError(validation.error);
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    const displayName = parentHabit
      ? `${parentHabit.habitDisplayName}: ${habitName.trim() || ''}`.trim()
      : (habitName.trim() || 'Habit Name');
    const effectiveIntervalUnit = intervalUnit || parentHabit?.intervalUnit || 'week';

    try {
      const request: CreateHabitRequest = {
        name: displayName,
        description,
        category: parentHabit ? undefined : selectedCategory,
        parentHabitId: parentHabit?.habitId,
        goalType: selectedGoalType,
        goalUnit,
        intervalUnit: effectiveIntervalUnit,
        publish,
      };

      // For child habits, use the child-specific goal amounts
      if (parentHabit) {
        if (selectedGoalType === 'THRESHOLD') {
          request.goalValue = parseFloat(childThresholdValue);
        } else {
          request.goalMin = parseFloat(childRangeMin);
          request.goalMax = parseFloat(childRangeMax);
        }
      } else {
        if (selectedGoalType === 'THRESHOLD') {
          request.goalValue = parseFloat(thresholdValue);
        } else {
          request.goalMin = parseFloat(rangeMin);
          request.goalMax = parseFloat(rangeMax);
        }
      }

      const response = await habitService.createHabit(request);

      if (!response.success) {
        setSaveError(response.error || 'Failed to create habit');
        return;
      }

      // If creating a child habit, also update the parent's goal if it changed
      if (parentHabit) {
        const parentGoalChanged = (
          selectedGoalType !== parentHabit.goal?.type ||
          goalUnit !== parentHabit.goalUnit ||
          effectiveIntervalUnit !== parentHabit.intervalUnit ||
          (selectedGoalType === 'THRESHOLD' && String(parentHabit.goal?.type === 'THRESHOLD' ? parentHabit.goal.value : '') !== thresholdValue) ||
          (selectedGoalType === 'RANGE' && (
            String(parentHabit.goal?.type === 'RANGE' ? parentHabit.goal.min : '') !== rangeMin ||
            String(parentHabit.goal?.type === 'RANGE' ? parentHabit.goal.max : '') !== rangeMax
          ))
        );

        if (parentGoalChanged && parentHabit.versionSK && parentHabit.lastModifiedAt) {
          const parentGoalRequest: UpdateHabitGoalRequest = {
            goalType: selectedGoalType,
            goalUnit,
            intervalUnit: effectiveIntervalUnit,
            versionSK: parentHabit.versionSK,
            lastModifiedAt: parentHabit.lastModifiedAt,
            isPublished: parentHabit.isPublished,
          };
          if (selectedGoalType === 'THRESHOLD') {
            parentGoalRequest.goalValue = parseFloat(thresholdValue);
          } else {
            parentGoalRequest.goalMin = parseFloat(rangeMin);
            parentGoalRequest.goalMax = parseFloat(rangeMax);
          }
          await habitService.updateGoal(parentHabit.habitId, parentGoalRequest);
        }
      }

      if (onHabitCreated) {
        onHabitCreated();
      }
      
      onClose();
    } catch (error: any) {
      setSaveError(error.message || 'Failed to create habit. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      ariaLabelledBy="create-habit-modal"
      ariaDescribedBy="create-habit-description"
    >
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <Box flex={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {parentHabit && (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: '"Oswald", sans-serif',
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    color: '#ffffff',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {parentHabit.habitDisplayName}:
                </Typography>
              )}
              <InputBase
                value={habitName}
                onChange={(e) => setHabitName(e.target.value)}
                onFocus={(e) => {
                  if (!parentHabit && e.target.value === 'Habit Name') {
                    e.target.select();
                  }
                }}
                placeholder={parentHabit ? 'Child Habit Name' : undefined}
                sx={{
                  width: '100%',
                  fontFamily: '"Oswald", sans-serif',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  color: '#ffffff',
                  borderBottom: '2px solid #32CA1D',
                  '& input': { padding: 0 },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Description Section */}
        <Box mb={4}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Description
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: '#1A1A1A',
              borderRadius: 1,
              border: '1px solid rgba(50, 202, 29, 0.3)',
            }}
          >
            {!parentHabit && (
              <Box sx={{ mb: 2 }}>
                {selectedCategory ? (
                  <Chip
                    label={selectedCategory}
                    variant="outlined"
                    size="medium"
                    color={getCategoryColor(selectedCategory)}
                    onClick={handleCategoryClick}
                    sx={{
                      fontSize: '1rem',
                      px: 2,
                      py: 1,
                      fontWeight: 500,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: '#32CA1D',
                        bgcolor: 'rgba(50, 202, 29, 0.1)',
                      },
                    }}
                  />
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCategoryClick}
                    sx={{
                      borderColor: 'rgba(50, 202, 29, 0.5)',
                      color: '#32CA1D',
                      '&:hover': {
                        borderColor: '#32CA1D',
                        bgcolor: 'rgba(50, 202, 29, 0.1)',
                      },
                    }}
                  >
                    Select Category
                  </Button>
                )}
              </Box>
            )}

            {parentHabit && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={parentHabit.category}
                  variant="outlined"
                  size="medium"
                  color={getCategoryColor(parentHabit.category)}
                  sx={{
                    fontSize: '1rem',
                    px: 2,
                    py: 1,
                    fontWeight: 500,
                    borderRadius: '6px',
                    cursor: 'default',
                    pointerEvents: 'none',
                  }}
                />
              </Box>
            )}

            {/* Category Selector Popover */}
            <Popover
              open={Boolean(categoryAnchorEl)}
              anchorEl={categoryAnchorEl}
              onClose={handleCategoryClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  bgcolor: '#1A1A1A',
                  border: '1px solid #32CA1D',
                  borderRadius: 1,
                  mt: 1,
                  maxHeight: 300,
                  overflow: 'auto',
                },
              }}
            >
              <Box sx={{ p: 1 }}>
                {categories.map((category) => (
                  <Box
                    key={category}
                    onClick={(e) => handleCategorySelect(category, e)}
                    sx={{
                      p: 0.5,
                      cursor: 'pointer',
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'rgba(50, 202, 29, 0.1)',
                      },
                    }}
                  >
                    <Chip
                      label={category}
                      variant="outlined"
                      size="medium"
                      color={getCategoryColor(category)}
                      sx={{
                        fontSize: '0.9rem',
                        px: 2,
                        py: 1,
                        fontWeight: 500,
                        borderRadius: '6px',
                        width: '100%',
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Popover>

            {/* Description Text Field */}
            <TextField
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your habit... (Markdown supported)"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                  color: '#ffffff',
                  '& fieldset': {
                    borderColor: 'rgba(50, 202, 29, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(50, 202, 29, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#32CA1D',
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Goal Section */}
        <Box mb={4}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Goal
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: '#1A1A1A',
              borderRadius: 1,
              border: '1px solid rgba(50, 202, 29, 0.3)',
            }}
          >
            {parentHabit ? (
              <ParentChildGoalEditor
                childHabits={[
                  {
                    habitId: '__new-child-habit__',
                    habitDisplayName: habitName
                      ? `${parentHabit.habitDisplayName}: ${habitName}`
                      : `${parentHabit.habitDisplayName}: Child Habit`,
                  },
                ]}
                childGoalAmounts={{
                  '__new-child-habit__': {
                    threshold: childThresholdValue,
                    min: childRangeMin,
                    max: childRangeMax,
                  },
                }}
                onChildGoalAmountChange={(_habitId, patch) => {
                  if (patch.threshold !== undefined) {
                    setChildThresholdValue(patch.threshold);
                  }
                  if (patch.min !== undefined) {
                    setChildRangeMin(patch.min);
                  }
                  if (patch.max !== undefined) {
                    setChildRangeMax(patch.max);
                  }
                  setSaveError(null);
                }}
                parentHabitDisplayName={parentHabit.habitDisplayName}
                selectedGoalType={selectedGoalType}
                onSelectedGoalTypeChange={(goalType) => setSelectedGoalType(goalType)}
                goalUnit={goalUnit}
                onGoalUnitChange={(value) => setGoalUnit(value)}
                intervalUnit={intervalUnit}
                onIntervalUnitChange={(value) => setIntervalUnit(value)}
                thresholdValue={thresholdValue}
                onThresholdValueChange={(value) => {
                  setThresholdValue(value);
                  setSaveError(null);
                }}
                rangeMin={rangeMin}
                onRangeMinChange={(value) => {
                  setRangeMin(value);
                  setSaveError(null);
                }}
                rangeMax={rangeMax}
                onRangeMaxChange={(value) => {
                  setRangeMax(value);
                  setSaveError(null);
                }}
                availableGoalUnits={availableGoalUnits}
                availableIntervalUnits={availableIntervalUnits}
                parentAmountsAsText
                childValidationError={childGoalValidationError}
              />
            ) : (
              <ParentChildGoalEditor
                childHabits={[]}
                childGoalAmounts={{}}
                onChildGoalAmountChange={() => {
                  // No-op: top-level creation has no child habit rows.
                }}
                selectedGoalType={selectedGoalType}
                onSelectedGoalTypeChange={(goalType) => setSelectedGoalType(goalType)}
                goalUnit={goalUnit}
                onGoalUnitChange={(value) => setGoalUnit(value)}
                intervalUnit={intervalUnit}
                onIntervalUnitChange={(value) => setIntervalUnit(value)}
                thresholdValue={thresholdValue}
                onThresholdValueChange={(value) => {
                  setThresholdValue(value);
                  setSaveError(null);
                }}
                rangeMin={rangeMin}
                onRangeMinChange={(value) => {
                  setRangeMin(value);
                  setSaveError(null);
                }}
                rangeMax={rangeMax}
                onRangeMaxChange={(value) => {
                  setRangeMax(value);
                  setSaveError(null);
                }}
                availableGoalUnits={availableGoalUnits}
                availableIntervalUnits={availableIntervalUnits}
                parentAmountsAsText={false}
                goalTypeSelectorPlacement="top"
              />
            )}
          </Box>
        </Box>

        {/* Error Message */}
        {saveError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {saveError}
          </Alert>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSaving}
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: '#ffffff',
              '&:hover': {
                borderColor: '#ffffff',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleSave(false)}
            disabled={isSaving}
            sx={{
              borderColor: 'rgba(255, 152, 0, 0.5)',
              color: '#FFB74D',
              '&:hover': {
                borderColor: '#FF9800',
                bgcolor: 'rgba(255, 152, 0, 0.1)',
              },
            }}
          >
            {isSaving ? 'Saving...' : 'Save as Draft'}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSave(true)}
            disabled={isSaving}
            sx={{
              bgcolor: '#32CA1D',
              '&:hover': {
                bgcolor: '#28a518',
              },
            }}
          >
            {isSaving ? 'Publishing...' : 'Publish'}
          </Button>
        </Stack>
      </Box>
    </GenericModal>
  );
};

export default CreateHabitModal;
